import { useState, useEffect } from "react";
import { Navigation } from "./Dashboard";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { 
  Brain, 
  CheckCircle, 
  XCircle, 
  Trophy,
  Star,
  RotateCcw,
  Zap
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Quiz() {
  const [niveau, setNiveau] = useState("débutant");
  const [quiz, setQuiz] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [quizComplete, setQuizComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  const startQuiz = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/quiz/generate?niveau=${niveau}&count=10`);
      if (response.ok) {
        const data = await response.json();
        setQuiz(data);
        setCurrentQuestion(0);
        setScore(0);
        setQuizComplete(false);
        setSelectedAnswer(null);
        setIsAnswered(false);
        setQuizResult(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Erreur lors du chargement du quiz");
      }
    } catch (error) {
      console.error("Error starting quiz:", error);
      toast.error("Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (answer) => {
    if (isAnswered) return;
    
    setSelectedAnswer(answer);
    setIsAnswered(true);
    
    const correct = quiz.questions[currentQuestion].correct_answer;
    if (answer === correct) {
      setScore(score + 1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < quiz.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setIsAnswered(false);
    } else {
      finishQuiz();
    }
  };

  const finishQuiz = async () => {
    setQuizComplete(true);
    setSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API}/quiz/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          score: score + (selectedAnswer === quiz.questions[currentQuestion].correct_answer ? 1 : 0),
          total: quiz.questions.length
        })
      });

      if (response.ok) {
        const result = await response.json();
        setQuizResult(result);
        if (result.new_badges?.length > 0) {
          toast.success(`Nouveau badge: ${result.new_badges.join(", ")}!`);
        }
      }
    } catch (error) {
      console.error("Error submitting quiz:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const finalScore = quizComplete 
    ? score + (selectedAnswer === quiz?.questions[currentQuestion]?.correct_answer ? 1 : 0)
    : score;

  const question = quiz?.questions[currentQuestion];
  const progress = quiz ? ((currentQuestion + 1) / quiz.questions.length) * 100 : 0;

  const getOptionClass = (option) => {
    if (!isAnswered) {
      return "hover:bg-primary/10 hover:border-primary/30";
    }
    
    const isCorrect = option === question.correct_answer;
    const isSelected = option === selectedAnswer;
    
    if (isCorrect) {
      return "bg-accent/10 border-accent text-accent";
    }
    if (isSelected && !isCorrect) {
      return "bg-destructive/10 border-destructive text-destructive";
    }
    return "opacity-50";
  };

  return (
    <div className="min-h-screen bg-background" data-testid="quiz-page">
      <Navigation />
      
      <main className="pt-20 pb-12 px-4">
        <div className="max-w-2xl mx-auto">
          {!quiz ? (
            // Quiz Start Screen
            <div className="animate-fade-in">
              <div className="text-center mb-12">
                <div className="w-20 h-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-6">
                  <Brain className="w-10 h-10 text-primary" />
                </div>
                <h1 className="font-heading text-3xl font-bold mb-4">Quiz Baoulé</h1>
                <p className="text-muted-foreground">
                  Testez vos connaissances et gagnez des points !
                </p>
              </div>

              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Choisissez votre niveau</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-3 gap-3">
                    {["débutant", "intermédiaire", "avancé"].map((level) => (
                      <button
                        key={level}
                        onClick={() => setNiveau(level)}
                        className={`p-4 rounded-xl border-2 transition-all ${
                          niveau === level
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-primary/30"
                        }`}
                      >
                        <span className="capitalize font-medium">{level}</span>
                      </button>
                    ))}
                  </div>

                  <div className="p-4 rounded-xl bg-muted/50">
                    <h4 className="font-medium mb-2">Règles du quiz</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• 10 questions à choix multiples</li>
                      <li>• +10 points par bonne réponse</li>
                      <li>• Débloquez des badges avec vos performances</li>
                    </ul>
                  </div>

                  <Button 
                    size="lg" 
                    className="w-full rounded-full"
                    onClick={startQuiz}
                    disabled={loading}
                    data-testid="start-quiz-btn"
                  >
                    {loading ? "Chargement..." : "Commencer le quiz"}
                  </Button>
                </CardContent>
              </Card>
            </div>
          ) : quizComplete ? (
            // Quiz Results Screen
            <div className="animate-scale-in text-center">
              <div className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6 ${
                finalScore >= quiz.questions.length * 0.8
                  ? "bg-accent/10"
                  : finalScore >= quiz.questions.length * 0.5
                  ? "bg-primary/10"
                  : "bg-destructive/10"
              }`}>
                <Trophy className={`w-12 h-12 ${
                  finalScore >= quiz.questions.length * 0.8
                    ? "text-accent"
                    : finalScore >= quiz.questions.length * 0.5
                    ? "text-primary"
                    : "text-destructive"
                }`} />
              </div>

              <h2 className="font-heading text-3xl font-bold mb-2">Quiz terminé !</h2>
              <p className="text-muted-foreground mb-8">
                {finalScore >= quiz.questions.length * 0.8
                  ? "Excellent travail !"
                  : finalScore >= quiz.questions.length * 0.5
                  ? "Bien joué !"
                  : "Continuez à pratiquer !"}
              </p>

              <Card className="border-border/50 mb-8">
                <CardContent className="p-8">
                  <div className="text-6xl font-heading font-bold mb-4">
                    {finalScore}/{quiz.questions.length}
                  </div>
                  <p className="text-muted-foreground mb-6">Bonnes réponses</p>

                  {quizResult && (
                    <div className="space-y-4">
                      <div className="flex justify-center items-center gap-2 text-primary">
                        <Star className="w-5 h-5" />
                        <span className="font-bold">+{quizResult.points_earned} points</span>
                      </div>
                      
                      {quizResult.new_badges?.length > 0 && (
                        <div className="p-4 rounded-xl bg-primary/5">
                          <p className="text-sm text-muted-foreground mb-2">Nouveaux badges :</p>
                          <div className="flex justify-center gap-2">
                            {quizResult.new_badges.map((badge) => (
                              <span 
                                key={badge}
                                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm badge-earned"
                              >
                                {badge}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {submitting && (
                    <p className="text-muted-foreground animate-pulse">
                      Enregistrement des résultats...
                    </p>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => setQuiz(null)}
                  className="rounded-full"
                >
                  Changer de niveau
                </Button>
                <Button
                  onClick={startQuiz}
                  className="rounded-full"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Rejouer
                </Button>
              </div>
            </div>
          ) : (
            // Quiz Questions
            <div className="animate-fade-in">
              {/* Progress Header */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-muted-foreground">
                    Question {currentQuestion + 1} sur {quiz.questions.length}
                  </span>
                  <div className="flex items-center gap-2 text-primary">
                    <Zap className="w-4 h-4" />
                    <span className="font-bold">{score * 10} pts</span>
                  </div>
                </div>
                <Progress value={progress} className="h-2" />
              </div>

              {/* Question Card */}
              <Card className="border-border/50 mb-6">
                <CardContent className="p-8">
                  <h2 className="font-heading text-2xl font-bold text-center mb-8">
                    {question.question}
                  </h2>

                  <div className="space-y-3">
                    {question.options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => handleAnswer(option)}
                        disabled={isAnswered}
                        className={`quiz-option w-full p-4 rounded-xl border-2 text-left transition-all ${getOptionClass(option)}`}
                        data-testid={`quiz-option-${index}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{option}</span>
                          {isAnswered && option === question.correct_answer && (
                            <CheckCircle className="w-5 h-5 text-accent" />
                          )}
                          {isAnswered && option === selectedAnswer && option !== question.correct_answer && (
                            <XCircle className="w-5 h-5 text-destructive" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Next Button */}
              {isAnswered && (
                <div className="text-center animate-slide-up">
                  <Button
                    size="lg"
                    onClick={handleNext}
                    className="rounded-full px-8"
                    data-testid="next-question-btn"
                  >
                    {currentQuestion < quiz.questions.length - 1 ? "Question suivante" : "Voir les résultats"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
